import { collection, getDocs, getDocsFromCache, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { User, AccessScope, isFullAccess } from '../types';

const chunk = <T>(arr: T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

// Carrega os clientes que o usuário PODE ver, filtrando no SERVIDOR — em vez de baixar a coleção
// inteira e filtrar no cliente (o custo passa a escalar com a carteira do usuário, não com a base toda).
// Espelha mobile/src/services/clientsQuery.ts.
// - Acesso total: só ATIVOS (`where active==true` — índice de campo único, automático, SEM composto).
//   Antes trazia ativos+inativos: os 4.600+ do e-commerce desativados + legado inflavam MUITO o
//   download do admin (Clientes levava minutos). `opts.includeInactive` traz tudo (escotilha p/ ver
//   um doc legado sem o campo `active`, ou reativar).
// - Vendedor: sellerId==uid OU createdBy==uid OU sankhya.codvend==codVendSankhya (número E string).
// - Gestor: sellerId IN (uids da equipe) OU sankhya.codvend IN (equipe — número E string), em lotes de 30.
//   Nesses ramos o `active` é filtrado NO CLIENTE (a carteira já é pequena; active na query exigiria
//   índice COMPOSTO por campo — evitamos).
// Tudo em queries de campo único (sem índice composto), mescladas/deduplicadas por id.
// Devolve objetos { id, ...data } crus (timestamps não convertidos — quem chama converte como já faz).
export async function fetchClientesDoUsuario(
  user: User | null,
  scope: AccessScope,
  opts?: { includeInactive?: boolean; fromCache?: boolean }
): Promise<any[]> {
  if (!user) return [];
  const col = collection(db, 'clients');
  const incInativos = opts?.includeInactive === true;
  const soAtivos = (arr: any[]) => (incInativos ? arr : arr.filter((c) => c.active !== false));
  // `fromCache`: lê só do cache local (IndexedDB) → instantâneo, para a pintura inicial. Cache vazio
  // devolve snapshot vazio (não rejeita), então a página cai no carregamento do servidor normalmente.
  const run = (q: any) => (opts?.fromCache === true ? getDocsFromCache(q) : getDocs(q));

  if (isFullAccess(user.role) || scope.full) {
    const snap = await run(incInativos ? query(col) : query(col, where('active', '==', true)));
    return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
  }

  const byId = new Map<string, any>();
  const addAll = (snaps: any[]) => snaps.forEach((sn) => sn.forEach((d: any) => byId.set(d.id, { id: d.id, ...d.data() })));

  if (user.role === 'seller') {
    const qs = [
      run(query(col, where('sellerId', '==', user.id))),
      run(query(col, where('createdBy', '==', user.id))),
    ];
    if (user.codVendSankhya != null) {
      // codvend pode estar como número OU string (legado) — consulta os dois p/ não perder cliente.
      qs.push(run(query(col, where('sankhya.codvend', '==', user.codVendSankhya))));
      qs.push(run(query(col, where('sankhya.codvend', '==', String(user.codVendSankhya)))));
    }
    addAll(await Promise.all(qs));
    return soAtivos(Array.from(byId.values()));
  }

  // Gestor: por sellerId (uid) e por sankhya.codvend (número E string), em lotes de 30 (limite do 'in').
  const ids = (scope.sellerIds || []).filter(Boolean);
  const cvStr = (scope.codvends || []).filter(Boolean);
  const cvNum = cvStr.map(Number).filter((n) => Number.isFinite(n));
  const qs: Promise<any>[] = [];
  for (const c of chunk(ids, 30)) qs.push(run(query(col, where('sellerId', 'in', c))));
  for (const c of chunk(cvNum, 30)) qs.push(run(query(col, where('sankhya.codvend', 'in', c))));
  for (const c of chunk(cvStr, 30)) qs.push(run(query(col, where('sankhya.codvend', 'in', c))));
  addAll(await Promise.all(qs));
  return soAtivos(Array.from(byId.values()));
}

// Carteira de UM vendedor específico — a aba "Rotas", onde o gestor simula o roteiro de alguém da
// hierarquia dele. Gêmea de `fetchCarteiraDeVendedor` em mobile/src/services/clientsQuery.ts, e espelha
// o MESMO recorte que o app aplica à carteira do vendedor (`sellerId` OU `createdBy` OU
// `sankhya.codvend`, este em número e string) — se divergir, a rota simulada roda sobre outro conjunto
// de clientes e não bate com a do app, que é justamente o que a aba promete.
//
// ⚠️ CADA CONSULTA COM O PRÓPRIO try/catch, e não um Promise.all que estoura junto. Regra do Firestore
// não FILTRA: ela recusa a consulta inteira que possa devolver um doc proibido. Para um gestor com
// escopo, `where('createdBy','==',alvo)` pode trazer um cliente que o alvo criou sem `sellerId` e com
// CODVEND fora de `equipeCodvends` — e aí `clienteDaEquipe()` não casa e a consulta INTEIRA morre,
// levando junto as outras duas. Isoladas, uma negada degrada a carteira em vez de zerar a tela.
// Devolve `{ clientes, negadas }`. O `negadas` NÃO é enfeite: sem ele, uma consulta recusada some em
// silêncio e a tela gera a rota sobre um pedaço da carteira, com cara de resultado completo. Numa tela
// de CONFERÊNCIA isso é pior que um erro — o gestor aprova uma semana que não existe.
export async function fetchCarteiraDeVendedor(
  alvo: { id: string; codVendSankhya?: number | null },
  opts?: { includeInactive?: boolean }
): Promise<{ clientes: any[]; negadas: number }> {
  if (!alvo?.id) return { clientes: [], negadas: 0 };
  const col = collection(db, 'clients');
  const byId = new Map<string, any>();
  let negadas = 0;
  const tentar = async (q: any) => {
    try {
      const sn = await getDocs(q);
      sn.forEach((d: any) => byId.set(d.id, { id: d.id, ...d.data() }));
    } catch { negadas++; /* negada ou indisponível: as demais seguem, mas o chamador fica sabendo */ }
  };

  const qs = [
    tentar(query(col, where('sellerId', '==', alvo.id))),
    tentar(query(col, where('createdBy', '==', alvo.id))),
  ];
  if (alvo.codVendSankhya != null) {
    // codvend existe como número E como string (legado) — consulta os dois p/ não perder cliente.
    qs.push(tentar(query(col, where('sankhya.codvend', '==', alvo.codVendSankhya))));
    qs.push(tentar(query(col, where('sankhya.codvend', '==', String(alvo.codVendSankhya)))));
  }
  await Promise.all(qs);
  const todos = Array.from(byId.values());
  return {
    clientes: opts?.includeInactive === true ? todos : todos.filter((c) => c.active !== false),
    negadas,
  };
}
